import React, { useEffect, useState, useRef } from 'react';
import HeroSection from '../components/HeroSection';
import OrthodonticCaseSlider from '../components/OrthodonticCaseSlider';
import { useLocation, useNavigate } from 'react-router-dom';

const OrthodonticsPage = () => {
  // URL 파라미터에서 초기 탭 설정 (깜빡임 방지)
  const location = useLocation();
  const navigate = useNavigate();
  
  const getInitialTab = () => {
    const urlParams = new URLSearchParams(location.search);
    const tabParam = urlParams.get('tab');
    return tabParam || 'non-extraction'; // 기본값: 'non-extraction'
  };

  const [activeTab, setActiveTab] = useState(getInitialTab);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [sliderHeight, setSliderHeight] = useState('auto');
  const heroRef = useRef(null);
  const slideRefs = useRef([]);

  // URL 변경 시 탭 상태 업데이트
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const tabParam = urlParams.get('tab');
    const newTab = tabParam || 'non-extraction';
    setActiveTab(newTab);
  }, [location.search]);

  useEffect(() => {
    // fullPage.js 제거
    if (window.$ && window.$.fn && window.$.fn.fullpage && window.$.fn.fullpage.destroy) {
      window.$.fn.fullpage.destroy('all');
    }
    
    document.body.classList.remove('fullpage-enabled');
    document.body.style.overflow = '';
    document.documentElement.style.overflow = '';
    
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, []);

  // activeTab이 변경될 때마다 페이지를 맨 위로 스크롤
  useEffect(() => {
    const timer = setTimeout(() => {
      if (heroRef.current) {
        heroRef.current.scrollIntoView({
          behavior: 'auto',
          block: 'start'
        });
      } else {
        window.scrollTo({
          top: 0,
          behavior: 'auto'
        });
      }
    }, 50);

    return () => clearTimeout(timer);
  }, [activeTab]);

  // 탭이 변경될 때 슬라이드 초기화
  useEffect(() => {
    setCurrentSlide(0);
  }, [activeTab]);

  // 슬라이드 높이 조정
  useEffect(() => {
    const updateSliderHeight = () => {
      const currentSlideElement = slideRefs.current[currentSlide];
      if (currentSlideElement) {
        const slideHeight = currentSlideElement.offsetHeight;
        setSliderHeight(`${slideHeight}px`);
      }
    };

    // DOM 업데이트 후 높이 계산
    const timer = setTimeout(updateSliderHeight, 100);
    
    // 윈도우 리사이즈 시에도 높이 재계산
    window.addEventListener('resize', updateSliderHeight);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updateSliderHeight);
    };
  }, [currentSlide, activeTab]);

  const tabs = [
    { id: 'non-extraction', name: '비발치 교정', shortName: '비발치' },
    { id: 'extraction', name: '발치 교정', shortName: '발치' },
    { id: 'partial', name: '부분 교정', shortName: '부분' },
    { id: 'orthodontic-implant', name: '교정 동반 임플란트', shortName: '교정임플' },
  ];

  // 탭 변경 핸들러 (URL도 함께 업데이트)
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    navigate(`/orthodontics?tab=${tabId}`, { replace: true });
  };

  // 각 탭별 사례 데이터
  const nonExtractionCases = [
    {
      id: '01',
      age: '10대',
      reason: '덧니도 있고 이가 삐뚤해요',
      treatment: '비발치 교정 (치료기간 2년)',
      beforeImage: '/assets/교정/비발치 교정/비발치 교정_01_치료전.webp',
      afterImage: '/assets/교정/비발치 교정/비발치 교정_01_치료후.webp'
    },
    {
      id: '02',
      age: '10대',
      reason: '덧니가 심하고 어금니가 돌아 났어요',
      treatment: '비발치 교정 (치료기간 2년)',
      beforeImage: '/assets/교정/비발치 교정/비발치 교정_02_치료전.webp',
      afterImage: '/assets/교정/비발치 교정/비발치 교정_02_치료후.webp'
    },
    {
      id: '03',
      age: '10대',
      reason: '앞니가 돌출되고 벌어졌어요',
      treatment: '비발치 교정 (치료기간 1년6개월)',
      beforeImage: '/assets/교정/비발치 교정/비발치 교정_03_치료전.webp',
      afterImage: '/assets/교정/비발치 교정/비발치 교정_03_치료후.webp'
    }
  ];

  const extractionCases = [
    {
      id: '01',
      age: '20대',
      reason: '앞니 돌출되었고, 이가 많이 삐뚤해요',
      treatment: '발치 교정 (치료기간 2년6개월)',
      beforeImage: '/assets/교정/발치 교정/발치 교정_01_치료전.webp',
      afterImage: '/assets/교정/발치 교정/발치 교정_01_치료후.webp'
    },
    {
      id: '02',
      age: '10대',
      reason: '위,아래 덧니가 심하고, 영구치가 없어요',
      treatment: '상악 발치 + 하악 비발치 교정 (치료기간 2년)',
      beforeImage: '/assets/교정/발치 교정/발치 교정_02_치료전.webp',
      afterImage: '/assets/교정/발치 교정/발치 교정_02_치료후.webp'
    }
  ];

  const partialCases = [
    {
      id: '01',
      age: '40대',
      reason: '아래 앞니가 많이 삐뚤해요',
      treatment: '하악 6전치 부분교정 (교정기간 6개월)',
      beforeImage: '/assets/교정/부분 교정/부분 교정_01_치료전.webp',
      duringImage: '/assets/교정/부분 교정/부분 교정_01_치료중.webp',
      afterImage: '/assets/교정/부분 교정/부분 교정_01_치료후.webp'
    }
  ];

  const orthodonticImplantCases = [
    {
      id: '01',
      age: '30대',
      reason: '치아가 없는 채로 방치해서 교합이 다 무너졌어요, 앞니 중앙선도 위아래가 달라요',
      treatment: '전악교정 + 임플란트',
      beforeImage: '/assets/교정/교정 동반 임플란트/교정 동반 임플란트_01_치료전.webp',
      afterImage: '/assets/교정/교정 동반 임플란트/교정 동반 임플란트_01_치료후.webp'
    },
    {
      id: '02',
      age: '30대',
      reason: '치아가 없는 채로 방치해서 교합이 다 무너졌어요',
      treatment: '전악교정 + 임플란트',
      beforeImage: '/assets/교정/교정 동반 임플란트/교정 동반 임플란트_02_치료전.webp',
      duringImage: '/assets/교정/교정 동반 임플란트/교정 동반 임플란트_02_치료중.webp',
      afterImage: '/assets/교정/교정 동반 임플란트/교정 동반 임플란트_02_치료후.webp'
    }
  ];

  const renderNonExtractionContent = () => (
    <div className="space-y-16">
      <div className="text-center">
        <h2 className="text-4xl font-bold text-primary mb-6">비발치 교정</h2>
        <div className="text-xl text-gray-600 max-w-5xl mx-auto leading-relaxed mb-8">
          <p className="mb-4 text-2xl font-semibold text-gray-800">"치아를 뽑아야 한다고 하는데, 정말 뽑아야만 할까요?"</p>
          <p className="mb-4">
            <strong className="text-primary">소중한 자연치아 28개를 모두 지키면서도 가지런한 치아가 가능합니다!</strong>
          </p>
          <p className="mb-4">
            발치 없이 치아의 위치를 정교하게 조정하고 턱의 공간을 효율적으로 활용하여<br />
            <strong className="text-secondary">건강하고 자연스러운 아름다운 미소</strong>를 만들어드립니다.
          </p>
          <p className="text-lg">
            3차원 정밀 분석을 통해 비발치 교정 가능성을 정확히 판단하고<br />
            <strong className="text-primary">당신의 치아에 가장 적합한 맞춤 치료 계획</strong>을 수립합니다.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-8">
          <div className="bg-emerald-50 rounded-lg p-6">
            <div className="w-12 h-12 bg-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
            </div>
            <h4 className="text-lg font-semibold text-gray-800 mb-2">28개 치아 모두 보존</h4>
            <p className="text-gray-600 text-sm">소중한 자연치아를 하나도 뽑지 않습니다</p>
          </div>
          
          <div className="bg-green-50 rounded-lg p-6">
            <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </div>
            <h4 className="text-lg font-semibold text-gray-800 mb-2">자연스러운 얼굴형</h4>
            <p className="text-gray-600 text-sm">부드럽고 조화로운 자연미 완성</p>
          </div>
          
          <div className="bg-teal-50 rounded-lg p-6">
            <div className="w-12 h-12 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <h4 className="text-lg font-semibold text-gray-800 mb-2">안전한 보존 치료</h4>
            <p className="text-gray-600 text-sm">최소 개입으로 최대 효과를 얻습니다</p>
          </div>
        </div>

        {/* 한앤손 부부치과만의 비발치 교정 */}
        <div className="bg-gradient-to-r from-emerald-50 via-green-50 to-teal-50 rounded-2xl p-8 max-w-4xl mx-auto">
          <h3 className="text-2xl font-bold text-primary mb-4">한앤손 부부치과만의 비발치 교정</h3>
          <div className="bg-white rounded-lg p-6 shadow-md">
            <p className="text-lg text-gray-700 mb-3">
              <strong className="text-secondary">"다른 곳에서 발치해야 한다고 했는데, 정말 그럴까요?"</strong>
            </p>
            <p className="text-lg text-gray-700">
              3차원 정밀 분석과 풍부한 임상경험으로<br />
              <strong className="text-primary">발치 없이도 가능한 새로운 치료 방법</strong>을 제시해드립니다.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-8">
        <h3 className="text-2xl font-bold text-center text-primary mb-8">비발치 교정 과정</h3>
        <div className="grid md:grid-cols-4 gap-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-lg">1</span>
            </div>
            <h4 className="font-semibold text-gray-800 mb-2">정밀 진단</h4>
            <p className="text-sm text-gray-600">3D CT와 모델 분석으로 공간 계산</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-lg">2</span>
            </div>
            <h4 className="font-semibold text-gray-800 mb-2">치료 계획</h4>
            <p className="text-sm text-gray-600">비발치 가능성 판단 및 계획 수립</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-lg">3</span>
            </div>
            <h4 className="font-semibold text-gray-800 mb-2">교정 치료</h4>
            <p className="text-sm text-gray-600">단계별 치아 이동 및 배열</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-cyan-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-lg">4</span>
            </div>
            <h4 className="font-semibold text-gray-800 mb-2">유지 관리</h4>
            <p className="text-sm text-gray-600">리테이너로 결과 유지</p>
          </div>
        </div>
      </div>

      <div>
        <OrthodonticCaseSlider 
          cases={nonExtractionCases}
          currentSlide={currentSlide}
          setCurrentSlide={setCurrentSlide}
          sliderHeight={sliderHeight}
          slideRefs={slideRefs}
          title="치료 사례"
          gradientColors="from-green-50 to-emerald-50"
        />
      </div>

      <div className="bg-primary text-white rounded-2xl p-10 text-center">
        <h3 className="text-2xl font-semibold mb-4">비발치 교정 상담받아보세요</h3>
        <p className="text-lg mb-6 text-white/90">
          치아를 뽑지 않고도 가지런하게 할 수 있을까 궁금하신가요?<br />
          정밀한 진단을 통해 비발치 교정 가능성을 확인해보세요.
        </p>
        <a 
          href="tel:043-534-3434" 
          className="inline-flex items-center bg-white text-primary px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors"
        >
          <svg className="w-5 h-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
            <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
          </svg>
          상담 예약하기
        </a>
      </div>
    </div>
  );

  const renderExtractionContent = () => (
    <div className="space-y-16">
      <div className="text-center">
        <h2 className="text-4xl font-bold text-primary mb-6">발치 교정</h2>
        <div className="text-xl text-gray-600 max-w-5xl mx-auto leading-relaxed mb-8">
          <p className="mb-4 text-2xl font-semibold text-gray-800">"앞니가 너무 나와서 고민이에요. 발치해야 할까요?"</p>
          <p className="mb-4">
            <strong className="text-primary">때로는 발치가 더 아름답고 건강한 결과를 만들어냅니다!</strong>
          </p>
          <p className="mb-4">
            심한 돌출이나 심각한 공간 부족의 경우, 정확한 계획 하에 발치를 통해<br />
            <strong className="text-secondary">극적이고 안정적인 변화</strong>를 경험하실 수 있습니다.
          </p>
          <p className="text-lg">
            어떤 치아를 언제 뽑을지, 뽑은 공간을 어떻게 활용할지<br />
            <strong className="text-primary">치밀한 계획과 정교한 기술로 완벽한 결과</strong>를 만들어드립니다.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-8">
          <div className="bg-blue-50 rounded-lg p-6">
            <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
              </svg>
            </div>
            <h4 className="text-lg font-semibold text-gray-800 mb-2">극적인 변화</h4>
            <p className="text-gray-600 text-sm">돌출 완전 개선과 완벽한 배열</p>
          </div>
          
          <div className="bg-indigo-50 rounded-lg p-6">
            <div className="w-12 h-12 bg-indigo-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <h4 className="text-lg font-semibold text-gray-800 mb-2">장기 안정성</h4>
            <p className="text-gray-600 text-sm">평생 유지되는 안정된 교합</p>
          </div>
          
          <div className="bg-purple-50 rounded-lg p-6">
            <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h4 className="text-lg font-semibold text-gray-800 mb-2">얼굴형 완전 변화</h4>
            <p className="text-gray-600 text-sm">이상적인 얼굴 비율과 밸런스</p>
          </div>
        </div>

        {/* 한앤손 부부치과만의 발치 교정 */}
        <div className="bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 rounded-2xl p-8 max-w-4xl mx-auto">
          <h3 className="text-2xl font-bold text-primary mb-4">한앤손 부부치과만의 발치 교정</h3>
          <div className="bg-white rounded-lg p-6 shadow-md">
            <p className="text-lg text-gray-700 mb-3">
              <strong className="text-secondary">"발치가 무서워서 망설여지시나요?"</strong>
            </p>
            <p className="text-lg text-gray-700">
              정확한 진단과 치밀한 계획으로<br />
              <strong className="text-primary">발치 후에도 자연스럽고 아름다운 결과</strong>를 보장해드립니다.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-8">
        <h3 className="text-2xl font-bold text-center text-primary mb-8">발치 교정 과정</h3>
        <div className="grid md:grid-cols-5 gap-4">
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-lg">1</span>
            </div>
            <h4 className="font-semibold text-gray-800 mb-2">정밀 분석</h4>
            <p className="text-sm text-gray-600">발치 필요성과 발치 부위 결정</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-indigo-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-lg">2</span>
            </div>
            <h4 className="font-semibold text-gray-800 mb-2">발치 계획</h4>
            <p className="text-sm text-gray-600">발치 순서와 시기 결정</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-lg">3</span>
            </div>
            <h4 className="font-semibold text-gray-800 mb-2">발치 및 교정 시작</h4>
            <p className="text-sm text-gray-600">계획에 따른 발치 후 교정</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-violet-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-lg">4</span>
            </div>
            <h4 className="font-semibold text-gray-800 mb-2">공간 폐쇄</h4>
            <p className="text-sm text-gray-600">발치 공간을 이용한 배열</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-fuchsia-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-lg">5</span>
            </div>
            <h4 className="font-semibold text-gray-800 mb-2">마무리</h4>
            <p className="text-sm text-gray-600">정밀한 교합 조정</p>
          </div>
        </div>
      </div>

      <div>
        <OrthodonticCaseSlider 
          cases={extractionCases}
          currentSlide={currentSlide}
          setCurrentSlide={setCurrentSlide}
          sliderHeight={sliderHeight}
          slideRefs={slideRefs}
          title="치료 사례"
          gradientColors="from-blue-50 to-indigo-50"
        />
      </div>

      <div className="bg-primary text-white rounded-2xl p-10 text-center">
        <h3 className="text-2xl font-semibold mb-4">발치 교정 상담받아보세요</h3>
        <p className="text-lg mb-6 text-white/90">
          돌출이나 심한 덧니 때문에 발치가 필요할까요?<br />
          정확한 진단과 계획으로 안전하고 확실한 교정 결과를 얻어보세요.
        </p>
        <a 
          href="tel:043-534-3434" 
          className="inline-flex items-center bg-white text-primary px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors"
        >
          <svg className="w-5 h-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
            <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
          </svg>
          상담 예약하기
        </a>
      </div>
    </div>
  );

  const renderPartialContent = () => (
    <div className="space-y-16">
      <div className="text-center">
        <h2 className="text-4xl font-bold text-primary mb-6">부분 교정</h2>
        <div className="text-xl text-gray-600 max-w-5xl mx-auto leading-relaxed mb-8">
          <p className="mb-4 text-2xl font-semibold text-gray-800">"전체 교정은 부담스럽고, 앞니 몇 개만 살짝 가지런하게 하고 싶어요"</p>
          <p className="mb-4">
            <strong className="text-primary">부분 교정으로 빠르고 경제적으로 원하는 부분만 완벽하게!</strong>
          </p>
          <p className="mb-4">
            앞니의 작은 틈이나 기울어진 치아, 약간의 돌출 등<br />
            <strong className="text-secondary">6개월 내외의 짧은 기간에 놀라운 변화</strong>를 경험하실 수 있습니다.
          </p>
          <p className="text-lg">
            시간도 비용도 부담도 줄이면서<br />
            <strong className="text-primary">원하는 부분의 완벽한 개선</strong>이 가능합니다.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-8">
          <div className="bg-fuchsia-50 rounded-lg p-6">
            <div className="w-12 h-12 bg-fuchsia-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h4 className="text-lg font-semibold text-gray-800 mb-2">초스피드 완성</h4>
            <p className="text-gray-600 text-sm">6개월 만에 완전히 새로운 미소</p>
          </div>
          
          <div className="bg-rose-50 rounded-lg p-6">
            <div className="w-12 h-12 bg-rose-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
              </svg>
            </div>
            <h4 className="text-lg font-semibold text-gray-800 mb-2">합리적 비용</h4>
            <p className="text-gray-600 text-sm">전체 교정의 절반 이하 비용</p>
          </div>
          
          <div className="bg-purple-50 rounded-lg p-6">
            <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
            </div>
            <h4 className="text-lg font-semibold text-gray-800 mb-2">정밀 타겟팅</h4>
            <p className="text-gray-600 text-sm">가장 눈에 띄는 부분만 집중 개선</p>
          </div>
        </div>

        {/* 한앤손 부부치과만의 부분 교정 */}
        <div className="bg-gradient-to-r from-fuchsia-50 via-pink-50 to-purple-50 rounded-2xl p-8 max-w-4xl mx-auto">
          <h3 className="text-2xl font-bold text-primary mb-4">한앤손 부부치과만의 부분 교정</h3>
          <div className="bg-white rounded-lg p-6 shadow-md">
            <p className="text-lg text-gray-700 mb-3">
              <strong className="text-secondary">"부분 교정도 제대로 해야 예쁩니다"</strong>
            </p>
            <p className="text-lg text-gray-700">
              단순히 빠르게만 하는 것이 아니라<br />
              <strong className="text-primary">전체적인 조화를 고려한 정교한 부분 교정</strong>을 시행합니다.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-8">
        <h3 className="text-2xl font-bold text-center text-primary mb-8">부분 교정 과정</h3>
        <div className="grid md:grid-cols-4 gap-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-fuchsia-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-lg">1</span>
            </div>
            <h4 className="font-semibold text-gray-800 mb-2">부분 진단</h4>
            <p className="text-sm text-gray-600">개선 가능 부위 정확 진단</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-rose-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-lg">2</span>
            </div>
            <h4 className="font-semibold text-gray-800 mb-2">장치 선택</h4>
            <p className="text-sm text-gray-600">부분용 교정 장치 선택</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-lg">3</span>
            </div>
            <h4 className="font-semibold text-gray-800 mb-2">집중 치료</h4>
            <p className="text-sm text-gray-600">목표 부위 집중 교정</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-lg">4</span>
            </div>
            <h4 className="font-semibold text-gray-800 mb-2">빠른 완성</h4>
            <p className="text-sm text-gray-600">단기간 내 결과 확인</p>
          </div>
        </div>
      </div>

      <div>
        <OrthodonticCaseSlider 
          cases={partialCases}
          currentSlide={currentSlide}
          setCurrentSlide={setCurrentSlide}
          sliderHeight={sliderHeight}
          slideRefs={slideRefs}
          title="치료 사례"
          gradientColors="from-purple-50 to-pink-50"
          showDuringImage={true}
        />
      </div>

      <div className="bg-primary text-white rounded-2xl p-10 text-center">
        <h3 className="text-2xl font-semibold mb-4">부분 교정 상담받아보세요</h3>
        <p className="text-lg mb-6 text-white/90">
          전체 교정은 부담스럽지만 앞니만이라도 가지런하게 하고 싶으신가요?<br />
          짧은 기간에 가장 눈에 띄는 개선 효과를 경험해보세요.
        </p>
        <a 
          href="tel:043-534-3434" 
          className="inline-flex items-center bg-white text-primary px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors"
        >
          <svg className="w-5 h-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
            <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
          </svg>
          상담 예약하기
        </a>
      </div>
    </div>
  );

  const renderOrthodonticImplantContent = () => (
    <div className="space-y-16">
      <div className="text-center">
        <h2 className="text-4xl font-bold text-primary mb-6">교정 동반 임플란트</h2>
        <div className="text-xl text-gray-600 max-w-5xl mx-auto leading-relaxed mb-8">
          <p className="mb-4 text-2xl font-semibold text-gray-800">"치아가 빠져서 옆 치아들이 다 기울어졌어요. 어떻게 해야 할까요?"</p>
          <p className="mb-4">
            <strong className="text-primary">교정과 임플란트의 완벽한 협업으로 모든 문제를 한 번에 해결!</strong>
          </p>
          <p className="mb-4">
            치아가 빠진 자리 때문에 기울어진 인접 치아를 먼저 바로 세우고<br />
            <strong className="text-secondary">최적의 위치에 임플란트를 식립</strong>하여 완벽한 교합을 완성합니다.
          </p>
          <p className="text-lg">
            단순히 빈 공간을 메우는 것이 아니라<br />
            <strong className="text-primary">무너진 교합을 완전히 회복시키는 종합적인 치료</strong>입니다.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-8">
          <div className="bg-cyan-50 rounded-lg p-6">
            <div className="w-12 h-12 bg-cyan-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
              </svg>
            </div>
            <h4 className="text-lg font-semibold text-gray-800 mb-2">교합 재건</h4>
            <p className="text-gray-600 text-sm">무너진 교합을 완전히 회복</p>
          </div>
          
          <div className="bg-teal-50 rounded-lg p-6">
            <div className="w-12 h-12 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
              </svg>
            </div>
            <h4 className="text-lg font-semibold text-gray-800 mb-2">최적 위치 식립</h4>
            <p className="text-gray-600 text-sm">가장 이상적인 위치에 임플란트 식립</p>
          </div>
          
          <div className="bg-sky-50 rounded-lg p-6">
            <div className="w-12 h-12 bg-sky-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <h4 className="text-lg font-semibold text-gray-800 mb-2">평생 안정성</h4>
            <p className="text-gray-600 text-sm">완벽한 교합으로 평생 사용</p>
          </div>
        </div>

        {/* 한앤손 부부치과만의 교정 동반 임플란트 */}
        <div className="bg-gradient-to-r from-cyan-50 via-teal-50 to-sky-50 rounded-2xl p-8 max-w-4xl mx-auto">
          <h3 className="text-2xl font-bold text-primary mb-4">한앤손 부부치과만의 교정 동반 임플란트</h3>
          <div className="bg-white rounded-lg p-6 shadow-md">
            <p className="text-lg text-gray-700 mb-3">
              <strong className="text-secondary">"복잡한 케이스일수록 더욱 정교한 계획이 필요합니다"</strong>
            </p>
            <p className="text-lg text-gray-700">
              교정과 임플란트의 순서와 타이밍을 정밀하게 계획하여<br />
              <strong className="text-primary">가장 이상적이고 안정적인 최종 결과</strong>를 만들어드립니다.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-gradient-to-r from-teal-50 to-sky-50 rounded-2xl p-8">
        <h3 className="text-2xl font-bold text-center text-primary mb-8">교정 동반 임플란트 과정</h3>
        <div className="grid md:grid-cols-4 gap-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-lg">1</span>
            </div>
            <h4 className="font-semibold text-gray-800 mb-2">종합 진단</h4>
            <p className="text-sm text-gray-600">교합 분석 및 임플란트 계획</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-cyan-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-lg">2</span>
            </div>
            <h4 className="font-semibold text-gray-800 mb-2">교정 치료</h4>
            <p className="text-sm text-gray-600">기울어진 치아 교정으로 공간 확보</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-sky-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-lg">3</span>
            </div>
            <h4 className="font-semibold text-gray-800 mb-2">임플란트 식립</h4>
            <p className="text-sm text-gray-600">최적 위치에 임플란트 시술</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-lg">4</span>
            </div>
            <h4 className="font-semibold text-gray-800 mb-2">최종 마무리</h4>
            <p className="text-sm text-gray-600">전체 교합 정밀 조정</p>
          </div>
        </div>
      </div>

      <div>
        <OrthodonticCaseSlider 
          cases={orthodonticImplantCases}
          currentSlide={currentSlide}
          setCurrentSlide={setCurrentSlide}
          sliderHeight={sliderHeight}
          slideRefs={slideRefs}
          title="치료 사례"
          gradientColors="from-teal-50 to-sky-50"
          showDuringImage={true}
        />
      </div>

      <div className="bg-primary text-white rounded-2xl p-10 text-center">
        <h3 className="text-2xl font-semibold mb-4">교정 동반 임플란트 상담받아보세요</h3>
        <p className="text-lg mb-6 text-white/90">
          빠진 치아 때문에 주변 치아까지 틀어졌나요?<br />
          교정과 임플란트를 함께하면 완벽한 해결책을 찾을 수 있습니다.
        </p>
        <a 
          href="tel:043-534-3434" 
          className="inline-flex items-center bg-white text-primary px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors"
        >
          <svg className="w-5 h-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
            <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
          </svg>
          상담 예약하기
        </a>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'non-extraction':
        return renderNonExtractionContent();
      case 'extraction':
        return renderExtractionContent();
      case 'partial':
        return renderPartialContent();
      case 'orthodontic-implant':
        return renderOrthodonticImplantContent();
      default:
        return renderNonExtractionContent();
    }
  };

  return (
    <div className="orthodontics-page-container">
      <HeroSection 
        title="교정치료"
        subtitle="가지런한 치아로 자신감 넘치는 미소를 완성하세요"
        backgroundImage="/assets/교정치료배경.png"
        brandText="한앤손 부부치과"
        ref={heroRef}
      >
        <div className="flex justify-center">
          <div className="bg-white/20 backdrop-blur-md rounded-2xl p-1 inline-flex max-w-full border border-white/30">
            <div className="flex space-x-1 overflow-x-auto scrollbar-hide">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`px-2.5 md:px-3 py-2 rounded-xl text-sm font-medium transition-all duration-200 whitespace-nowrap min-w-0 flex-shrink-0 ${
                    activeTab === tab.id
                      ? 'bg-white text-primary shadow-md'
                      : 'text-white/90 hover:text-white hover:bg-white/20'
                  }`}
                >
                  <span className="block md:hidden">{tab.shortName}</span>
                  <span className="hidden md:block">{tab.name}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </HeroSection>

      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="transition-all duration-300">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default OrthodonticsPage; 